import { RANGE, convertISOToPicker } from 'js/components/common/Fields/DatePicker';
import { FORM_FIELD_TYPE, FORM_RULE_CONDITION_OPERATOR } from 'js/components/common/Form/constants';
import { confirm, length, regexp, required, range, money, number, email, fn } from 'js/utils/Validation/validationRules';

import { MIN_AGE, SUPPORTED_FIELD_TYPES } from './constants';

export const cmsDateStringToDate = (dateStr) => {
    if (dateStr === 'today') {
        return new Date();
    }

    const dateISORegexp = /^(\d{4})[-](0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])$/; // ISO format yyyy-mm-dd
    const isMatchedISOFormat = dateISORegexp.test(dateStr);

    if (isMatchedISOFormat) {
        return new Date(dateStr);
    }

    const periodFormatRegexp = /^([+|-]{1}\d+)([dwmy])$/; // +/-[number]d/w/m/y
    const periodArr = dateStr.match(periodFormatRegexp);

    if (periodArr) {
        const factor = Number(periodArr[1]);
        const date = new Date();
        let y = date.getFullYear();
        let m = date.getMonth();
        let d = date.getDate();

        switch (periodArr[2]) {
            case 'm':
                m += factor;
                break;
            case 'd':
                d += factor;
                break;
            case 'w':
                d += 7 * factor;
                break;
            case 'y':
                y += factor;
                break;
            default:
                return null;
        }

        return new Date(y, m, d);
    }

    return null;
};

export const getCountriesList = (allowedCountries) => allowedCountries
    .map(({ code, name }) => ({ value: code, label: name }));

export const getPhoneAreaCodeList = (allowedCountries) => allowedCountries
    .map(({ code, phoneAreaCode }) => ({ value: code, label: phoneAreaCode }));

export const getCurrenciesList = (allowedCurrencies) => allowedCurrencies
    .map((currency) => ({ value: currency, label: currency }));

export const imsToFieldValue = (fieldValue, field, additionalData = {}) => {
    const { allowedCurrencies, allowedCountries } = additionalData;

    switch (field.type) {
        case FORM_FIELD_TYPE.CHECKBOX:
            if (field.imsValueChecked === fieldValue) {
                return true;
            } else if (field.imsValueUnchecked === fieldValue) {
                return false;
            }

            return null;
        case FORM_FIELD_TYPE.DROPDOWN: {
            const { selectedList, defaultValue, customList } = field;
            let list = customList;

            if (selectedList === 'countries') {
                list = getCountriesList(allowedCountries);
            } else if (selectedList === 'currencies') {
                list = getCurrenciesList(allowedCurrencies);
            }

            const possibleValues = list.map(({ value }) => value);

            if (!possibleValues.includes(fieldValue)) {
                return possibleValues.includes(defaultValue)
                    ? defaultValue
                    : '';
            }

            return fieldValue || '';
        }
        case FORM_FIELD_TYPE.RADIO_BUTTON: {
            const { customList, defaultValue } = field;
            const possibleValues = customList.map(({ value }) => value);

            if (!possibleValues.includes(fieldValue)) {
                return defaultValue;
            }

            return fieldValue;
        }
        default: return fieldValue || '';
    }
};

export const imsToPhoneFieldValue = (fieldValue, field, additionalData) => {
    const { allowedCountries, countrycode } = additionalData;

    const countryByPhoneNumber = allowedCountries
        .filter(country => fieldValue.startsWith(country.phoneAreaCode))
        .sort((a, b) => b.phoneAreaCode.length - a.phoneAreaCode.length)?.[0];

    if (countryByPhoneNumber) {
        return [countryByPhoneNumber.code, fieldValue.slice(countryByPhoneNumber.phoneAreaCode.length)];
    }

    const countryByPhoneNumberWithoutPlus = allowedCountries
        .filter(country => fieldValue.startsWith(country.phoneAreaCode.replace('+', '')))
        .sort((a, b) => b.phoneAreaCode.length - a.phoneAreaCode.length)?.[0];

    if (countryByPhoneNumberWithoutPlus) {
        return [countryByPhoneNumberWithoutPlus.code, fieldValue.slice(countryByPhoneNumberWithoutPlus.phoneAreaCode.length - 1)];
    }

    return [countrycode, ''];
};

export const fieldToImsValue = (formData, field, allowedCountries) => {
    const fieldValue = formData[field.name];

    switch (field.type) {
        case FORM_FIELD_TYPE.CHECKBOX: return fieldValue ? field.imsValueChecked || true : field.imsValueUnchecked || false;
        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH: {
            const { year, month, day } = convertISOToPicker(fieldValue);
            const areSomePartNotSet = [year, month, day]
                .map(part => parseInt(part))
                .some(part => isNaN(part));
            return areSomePartNotSet ? null : fieldValue;
        }
        case FORM_FIELD_TYPE.PHONE:
        case FORM_FIELD_TYPE.CELLPHONE: {
            const phoneCode = allowedCountries.find(country => country.code === formData[`${field.name}-areaCode`]).phoneAreaCode;
            const phone = formData[field.name];

            return phone ? `${phoneCode}${phone}` : '';
        }
        default: return fieldValue || '';
    }
};

export const getTagExpirationDate = (expirationDate, formatter) => {
    if (!expirationDate || Number.isNaN(expirationDate)) {
        return;
    }

    const date = new Date();

    date.setDate(date.getDate() + Number(expirationDate));

    return formatter.date(date.toISOString(), '%Y-%m-%d');
};

export const getExtraImsFields = (extraImsFields = '', value) => {
    return extraImsFields
        .split(',')
        .filter(Boolean)
        .reduce((accumulator, extraImsFieldName) => ({
            ...accumulator,
            [extraImsFieldName.trim()]: value
        }), {});
};

export const getRangeFromValidation = (field) => {
    const dateRangeRule = field.validation && field.validationRules?.find((validationRule) => validationRule.type === 'dateRange');

    if (!dateRangeRule) {
        return RANGE.PAST;
    }

    const minDate = cmsDateStringToDate(dateRangeRule.min);
    const maxDate = cmsDateStringToDate(dateRangeRule.max);

    if (!minDate || !maxDate) {
        return RANGE.PAST;
    }

    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();

    return `${minYear}-${maxYear}`;
};

export const getBirthDateRange = (dynamicTags) => {
    const dtagMinAge = dynamicTags.DTAG_USER_MIN_AGE_VALIDATION;
    const minAge = dtagMinAge ? Number(dtagMinAge) : MIN_AGE;
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear - minAge;
    const minYear = currentYear - 100;

    return `${minYear}-${maxYear}`;
};

export const selectValidation = (validationRules = [], field) => {
    return validationRules
        .map((validation) => {
            const error = validation.message;

            switch (validation.type) {
                case 'required': {
                    if (field.baseType === FORM_FIELD_TYPE.CHECKBOX) {
                        return required({
                            ...validation,
                            error,
                            predicate: value => value
                        });
                    }

                    if (field.baseType === FORM_FIELD_TYPE.DATE_PICKER || field.baseType === FORM_FIELD_TYPE.DATE_OF_BIRTH) {
                        return required({
                            ...validation,
                            error,
                            predicate: (value) => {
                                const [year = '', month = '', day = ''] = value?.split('-') || [];

                                return year && month && day;
                            }
                        });
                    }

                    return required({ ...validation, error });
                }
                case 'length':
                    return length({ ...validation, error });
                case 'range':
                    return range({ ...validation, error });
                case 'dateRange':
                    return range({
                        ...validation,
                        error,
                        includeMin: true,
                        includeMax: true,
                        parse: (value) => cmsDateStringToDate(value)
                    });
                case 'regexp': {
                    let re = validation.regexp;

                    if (typeof re === 'string' && re.startsWith('/')) {
                        const flags = re.substring(re.lastIndexOf('/') + 1);

                        re = new RegExp(re.slice(1, re.length - flags.length - 1), flags);
                    }

                    return regexp({ ...validation, re, error });
                }
                case 'confirm':
                    return confirm({ ...validation, error });
                case 'amount':
                    return money({ ...validation, error });
                case 'number':
                    return number({ ...validation, error });
                case 'email':
                    return email({ ...validation, error });
                case 'fn':
                    return fn({ ...validation });
                default:
                    return null;
            }
        })
        .filter(Boolean);
};

export const getFormFields = (formFields) => formFields
    .filter((field) => field.active && SUPPORTED_FIELD_TYPES.includes(field.type));

export const getFormRules = (formRules, formFields) => {
    const actualFormFields = getFormFields(formFields);
    const formFieldsNames = actualFormFields.map(formField => formField.name);

    return formRules
        .map((rule) => ({
            ...rule,
            actions: rule.actions.filter(action => formFieldsNames.includes(action.field)),
            conditions: rule.conditions.filter(condition => formFieldsNames.includes(condition.field))
        }))
        .filter((rule) => rule.active && rule.actions.length && rule.conditions.length);
};

export const getHiddenByRulesFields = ({ rules = [], values = {} }) => {
    const fieldsVisibilityMap = rules.reduce((acc, rule) => {
        // apply conditions by AND
        const areConditionsMet = rule.conditions.every(condition => {
            const { operator, field, value } = condition;

            switch (operator) {
                case FORM_RULE_CONDITION_OPERATOR.equal:
                    return values[field] === value;
                case FORM_RULE_CONDITION_OPERATOR.notEqual:
                    return values[field] !== value;
                default:
                    return false;
            }
        });

        rule.actions.forEach(action => {
            // apply rules by OR
            acc[action.field] = acc[action.field] || areConditionsMet;
        });

        return acc;
    }, {});

    rules.forEach(rule => {
        rule.conditions.forEach(condition => {
            if ([condition.field] in fieldsVisibilityMap && !fieldsVisibilityMap[condition.field]) {
                rule.actions.forEach(action => {
                    fieldsVisibilityMap[action.field] = false;
                });
            }
        });
    });

    return Object.keys(fieldsVisibilityMap)
        .filter(key => !fieldsVisibilityMap[key])
        .sort();
};
